import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Compass, RefreshCw, Inbox, Lock, LogOut, Loader2, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "wf_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);

  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (tk) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/enquiries`, { headers: { Authorization: `Bearer ${tk}` } });
      setRows(data);
      setAuthed(true);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setAuthed(false);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch (err) {
      setError(err.response?.status === 401 ? "Incorrect password." : "Login failed. Try again.");
    }
    setLoggingIn(false);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setRows([]);
  };

  const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const downloadCsv = () => {
    const cols = ["created_at", "name", "email", "organisation", "need", "location", "dates", "details", "email_sent"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.join(",")];
    rows.forEach((r) => lines.push(cols.map((c) => esc(r[c])).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wayfinder-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <main className="min-h-screen bg-brand-navy flex flex-col items-center justify-center px-6" data-testid="admin-login-page">
        <a href="/" className="flex items-center gap-3 mb-10">
          <Compass className="w-6 h-6 text-brand-aqua" />
          <span className="font-serif text-lg text-brand-sand">WayFinder <span className="text-brand-teal">Pacific</span></span>
        </a>
        <form onSubmit={login} className="w-full max-w-sm border border-brand-teal/20 p-8" data-testid="admin-login-form">
          <div className="flex items-center gap-2 text-brand-teal">
            <Lock className="w-4 h-4" />
            <span className="font-sans text-xs tracking-[0.28em] uppercase">Admin access</span>
          </div>
          <h1 className="mt-4 font-serif text-3xl text-brand-sand">Enter password</h1>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus
            placeholder="Password" data-testid="admin-password-input"
            className="mt-6 w-full bg-transparent border border-brand-teal/30 px-4 py-3 font-sans text-brand-sand placeholder:text-brand-sand/40 focus:border-brand-aqua focus:outline-none" />
          {error && <p className="mt-3 font-sans text-sm text-brand-coral" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={loggingIn} data-testid="admin-login-submit"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-coral text-brand-navy font-medium hover:bg-brand-sand transition-colors disabled:opacity-60">
            {loggingIn ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</> : "Unlock"}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-brand-navy" data-testid="admin-page">
      <header className="border-b border-brand-teal/20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <Compass className="w-6 h-6 text-brand-aqua" />
            <span className="font-serif text-lg text-brand-sand">WayFinder <span className="text-brand-teal">Pacific</span></span>
          </a>
          <div className="flex items-center gap-3">
            <button onClick={downloadCsv} disabled={rows.length === 0} data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 border border-brand-teal/40 px-4 py-2 text-sm text-brand-sand hover:bg-brand-teal hover:text-brand-navy transition-colors disabled:opacity-40">
              <Download className="w-4 h-4" /> CSV
            </button>
            <button onClick={() => load(token)} data-testid="admin-refresh"
              className="inline-flex items-center gap-2 border border-brand-teal/40 px-4 py-2 text-sm text-brand-sand hover:bg-brand-teal hover:text-brand-navy transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-brand-coral/40 px-4 py-2 text-sm text-brand-coral hover:bg-brand-coral hover:text-brand-navy transition-colors">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="font-sans text-xs tracking-[0.28em] uppercase text-brand-teal">Enquiries</span>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight text-brand-sand">Incoming leads</h1>
          </div>
          <span className="font-serif text-5xl text-brand-teal/40" data-testid="admin-count">{String(rows.length).padStart(2, "0")}</span>
        </div>

        {!loading && rows.length === 0 && (
          <div className="mt-16 flex flex-col items-center justify-center py-24 border border-brand-teal/20 text-brand-sand/60">
            <Inbox className="w-10 h-10 mb-4 text-brand-teal/50" />
            <p className="font-sans">No enquiries yet.</p>
          </div>
        )}

        <div className="mt-12 space-y-4">
          {rows.map((r, i) => (
            <div key={r.id} className="border border-brand-teal/20 p-6 hover:bg-surface transition-colors" data-testid={`admin-enquiry-${i}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div className="flex items-baseline gap-4 flex-wrap">
                  <span className="font-serif text-brand-teal/50 text-lg">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-serif text-2xl text-brand-sand">{r.name}</h3>
                  <span className="font-sans text-xs px-3 py-1 border border-brand-coral/50 text-brand-coral">{r.need}</span>
                  {r.email_sent === false && (
                    <span className="font-sans text-xs px-2 py-1 bg-brand-coral/20 text-brand-coral" data-testid={`admin-notemailed-${i}`}>Not emailed</span>
                  )}
                  {r.email_sent === true && (
                    <span className="font-sans text-xs px-2 py-1 text-brand-teal" data-testid={`admin-emailed-${i}`}>✓ Emailed</span>
                  )}
                </div>
                <span className="font-sans text-xs text-brand-sand/50">{fmt(r.created_at)}</span>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 font-sans text-sm">
                <Field label="Email" value={r.email} />
                <Field label="Organisation" value={r.organisation} />
                <Field label="Island / location" value={r.location} />
                <Field label="Rough dates" value={r.dates} />
              </div>
              {r.details && (
                <div className="mt-4">
                  <p className="font-sans text-xs tracking-[0.15em] uppercase text-brand-teal">Details</p>
                  <p className="mt-1 font-sans text-brand-sand/80 leading-relaxed">{r.details}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}

const Field = ({ label, value }) => (
  <div>
    <p className="font-sans text-xs tracking-[0.15em] uppercase text-brand-teal">{label}</p>
    <p className="mt-1 text-brand-sand/80 break-words">{value || "—"}</p>
  </div>
);
